import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DefaultApiLimit from '../../../config/defaultLimit';
import {
  RequestSummaryReportInterFace,
  SummaryReportInterFace,
} from '../../../interface/summary-reports-interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import {
  disableSummaryReportFiltersApplied,
  fetchSummaryReport,
  fetchListSummaryReportFilters,
  updateSummaryReportFilters,
  setSortData,
} from '../../../redux/slice/SummaryReportSlice';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import { changeDateTimeFormat } from '../../../shared-functions/DataTime';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import ToFixedValue from '../../../shared-functions/ToFixedValue';
import stationManagementApi from '../../../utils/api/station-management';

interface ReportLeftTopContent {
  leftTopContent?: JSX.Element;
}

/**
 * ReportsSummary
 * @param {ReportLeftTopContent} properties The Component properties
 * @returns {any} The Reports Summary component.
 */
function ReportsSummary(properties: ReportLeftTopContent) {
  const { leftTopContent } = properties;
  const { t } = useTranslation('stationManagement');
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const { selectedChargingPoint } = useAppSelector((state) => state.stationManagement);
  const dispatch = useAppDispatch();
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const {
    SummaryReport,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.SummaryReports);
  const column = [
    {
      label: t('dateRange'),
      accessorKey: 'date' as keyof SummaryReportInterFace,
      sortAllow: false,
      /**
       * Date range (start date to end date)
       */
      customData: (currentRow: SummaryReportInterFace) => (
        <p>
          {changeDateTimeFormat(currentRow?.startDate)} to{' '}
          {changeDateTimeFormat(currentRow?.endDate)}
        </p>
      ),
    },
    {
      label: t('devicesName'),
      accessorKey: 'chargingPointName' as keyof SummaryReportInterFace,
      sortAllow: true,
    },
    {
      label: t('deviceSn'),
      accessorKey: 'chargingPointSn' as keyof SummaryReportInterFace,
      sortAllow: true,
    },
    {
      label: t('stationLocation'),
      accessorKey: 'chargingPointLocation' as keyof SummaryReportInterFace,
      sortAllow: true,
    },
    {
      label: t('totalUptime'),
      accessorKey: 'totalUptimeMin' as keyof SummaryReportInterFace,
      sortAllow: true,
      /**
       * KWh Consumed
       */
      customData: (currentRow: SummaryReportInterFace) => {
        return <p>{currentRow?.totalUptimeMin}</p>;
      },
    },
    {
      label: t('totalRevenue'),
      accessorKey: 'totalRevenue' as keyof SummaryReportInterFace,
      sortAllow: true,
    },
    {
      label: t('totalEnergyDispensed'),
      accessorKey: 'totalPowerConsumed' as keyof SummaryReportInterFace,
      sortAllow: true,
      /**
       * KWh Consumed
       */
      customData: (currentRow: SummaryReportInterFace) => {
        const value = Number(currentRow?.totalPowerConsumed) / 1000;
        return (
          <div className="amount-in-transaction-table">
            <p className="table-row-each-data">
              {ToFixedValue(value)}
            </p>
            <span>{t('kwh')}</span>
          </div>
        );
      },
    },
    {
      label: t('avgChargingSessionsPerDay'),
      accessorKey: 'averageTransactionDurationInMinutes' as keyof SummaryReportInterFace,
      sortAllow: true,
      /**
       * KWh Consumed
       */
      customData: (currentRow: SummaryReportInterFace) => {
        return <p>{ToFixedValue(currentRow?.averageTransactionDurationInMinutes)}</p>;
      },
    },
    {
      label: t('avgSessionDuration'),
      accessorKey: 'totalSessions' as keyof SummaryReportInterFace,
      sortAllow: true,
    },
  ];

  /**
   * Handle the fetch the data in api function
   * @param {RequestSummaryReportInterFace} data The request payload data.
   */
  const fetchData = (data: RequestSummaryReportInterFace) => {
    if (!loading && selectedChargingPoint) {
      dispatch(fetchSummaryReport({ ...data, chargingStation: selectedChargingPoint?.id }));
    }
  };

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('dateRange'), accessorKey: 'startDate' },
    { label: t('devicesName'), accessorKey: 'chargingPointName' },
    { label: t('deviceSn'), accessorKey: 'chargingPointSn' },
    { label: t('stationLocation'), accessorKey: 'chargingPointLocation' },
    { label: t('totalUptime'), accessorKey: 'totalUptimeMin' },
    { label: t('totalRevenue'), accessorKey: 'totalRevenue' },
    { label: t('totalEnergyDispensed'), accessorKey: 'totalPowerConsumed' },
    { label: t('avgChargingSessionsPerDay'), accessorKey: 'averageTransactionDurationInMinutes' },
    { label: t('avgSessionDuration'), accessorKey: 'totalSessions' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );
  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    setExportLoader(true);
    stationManagementApi
      .fetchReportSummaryExport({
        ...queryParameters,
        submissionStatus: 'approved',
        sortBy,
        sortField,
        chargingStation: selectedChargingPoint?.id,
      })
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: SummaryReportInterFace, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'startDate':
                return `${changeDateTimeFormat(row?.startDate)} to ${changeDateTimeFormat(row?.endDate)}`;
              case 'totalPowerConsumed':
                return `${ToFixedValue(Number(row?.totalPowerConsumed) / 1000)} Kwh`;
              case 'averageTransactionDurationInMinutes':
                return `${ToFixedValue(row?.averageTransactionDurationInMinutes)}`;
              default:
                return (row as any)[col?.accessorKey] || 'N/A';
            }
          }),
        );
        if (type === 'csv') {
          exportCSV(headers, rows, 'charging-station-approved.csv');
        } else {
          exportPDF(headers, rows, 'charging-station-approved.pdf');
        }
        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };
  /**
   * Function to handle data limit change
   */
  const handleLimitChange = (changeLimit: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page: DefaultApiLimit.page,
      limit: changeLimit,
      sortBy,
      sortField,
    });
  };
  /**
   * Function to handle page change
   * @param {number} pageNumber The page number to navigate to.
   */
  const handlePageChange = (pageNumber: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page: pageNumber,
      limit,
      sortBy,
      sortField,
    });
  };

  /**
   * Function for sorting table data
   * @param {object} sortData The sorting configuration.
   * @param {'asc' | 'desc'} sortData.sortBy The sort direction.
   * @param {string} sortData.sortField The sort field column.
   */
  const handleSortFunction = (sortData: { sortBy: 'asc' | 'desc'; sortField: string }) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };
  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortBy, sortField });
  };

  useEffect(() => {
    if (!selectedChargingPoint?.id) return;

    const currentId = selectedChargingPoint.id;
    const lastId = sessionStorage.getItem('SummaryReportFilters'); // string | null

    // If same id as last time -> do nothing
    if (lastId !== currentId || filters.length === 1) {
      dispatch(fetchListSummaryReportFilters({ chargingStation: currentId }));
    }
    if (SummaryReport.length === 0 || lastId !== currentId || isFilterUpdated) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
      dispatch(disableSummaryReportFiltersApplied());
    }
    sessionStorage.setItem('SummaryReportFilters', currentId);
  }, [selectedChargingPoint?.id, filters]);
  return (
    <div>
      {' '}
      <CustomTable
        version="v2"
        currentPage={page}
        header={column}
        data={SummaryReport}
        serialNo
        sizeChangeButtonRequired
        limit={limit}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        loading={loading}
        filterListLoading={filterListLoading}
        filters={filters}
        filtersToDispatch={updateSummaryReportFilters}
        filterListToDispatch={fetchListSummaryReportFilters}
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        exportLoader={exportLoader}
        refreshCurrentPages={handleRefresh}
        leftTopContent={leftTopContent}
        handlePageChange={handlePageChange}
        handleLimitChange={handleLimitChange}
        topHeader={false}
        sortData={{
          sortBy,
          sortField,
        }}
        handleSortChange={handleSortFunction}
        paginate
        flexDirection
        onHiddenColumnsChange={setHiddenColumns}
        tableHeight="calc(100vh - 340px)"
      />
    </div>
  );
}

export default ReportsSummary;
